import type { VoidComponent } from "solid-js";
import { Show } from "solid-js";
import type { ExtendableInputProps } from "./Input";
import { Icon, type IconName } from "../icon/Icon";
import Text from "../text/Text";
import { isNil } from "lodash-es";
import type { FileError, FileMimeType } from "@zag-js/file-utils";
import { FileUpload, FileUploadContext } from "@ark-ui/solid";
import Input from "./Input";
import { TextAndIconButton, TextButton } from "../button/Button";
import { useTranslation } from "../../state/i18n";

// Unlike FileInput, this component does not upload the file, it selects it and returns the
// raw File back to the parent via receiveFile.
// Error state is also owned by the parent (setError).
// The two could be unified with a discriminated union once upload-on-select is needed here too.
type UploadFileInputProps = {
  value?: {
    filename: string;
    mediaTypeIcon: IconName;
  };
  accept: FileMimeType[];
  maxFileSizeInBytes: number;
  uploadActionLabel: string;
  receiveFile: (file: File | undefined) => void;
  setError: (fileError: FileError | undefined) => void;
} & ExtendableInputProps;

const UploadFileInput: VoidComponent<UploadFileInputProps> = (props) => {
  const { t } = useTranslation();

  return (
    <Input {...props} errorMessage={props.errorMessage}>
      {(id) => (
        <FileUpload.Root
          id={id}
          maxFiles={1}
          maxFileSize={props.maxFileSizeInBytes}
          accept={props.accept}
          onFileAccept={(details) => {
            props.setError(undefined);
            props.receiveFile(details.files[0]);
          }}
          onFileReject={(details) => props.setError(details.files[0]?.errors[0])}
        >
          <FileUploadContext>
            {(context) => (
              <Show
                when={props.value}
                fallback={
                  <div class="rounded-sm border border-transparent">
                    <FileUpload.Trigger
                      asChild={(triggerProps) => (
                        <TextAndIconButton
                          block
                          variant="secondary"
                          icon="upload"
                          text={props.uploadActionLabel}
                          {...triggerProps}
                        />
                      )}
                    />
                  </div>
                }
              >
                {(value) => (
                  <div
                    class="flex items-center gap-1.5 rounded-sm border bg-surface-extra-subtle py-2 pr-2 pl-3"
                    classList={{
                      "border-subtle": isNil(props.errorMessage),
                      "border-error": !isNil(props.errorMessage),
                    }}
                  >
                    <Icon class="flex-none" name={value().mediaTypeIcon} type="solid" size="lg" />
                    <Text class="text-base-regular text-primary" text={value().filename} truncate />
                    <div class="ml-auto">
                      <TextButton
                        variant="tertiary"
                        text={t(($) => $.FILE_INPUT_ACTION_CHANGE_FILE)}
                        onClick={() => context().openFilePicker()}
                        size="medium"
                      />
                    </div>
                  </div>
                )}
              </Show>
            )}
          </FileUploadContext>
          <FileUpload.HiddenInput data-testid="fileInput" />
        </FileUpload.Root>
      )}
    </Input>
  );
};

export default UploadFileInput;
